import React from 'react';
import logoFull from '../../../assets/images/logo-full.png';
import RemotePath from '../../../consts/RemotePaths';

/**
 * Monta linhas de localização a partir dos campos disponíveis.
 * Retorna um array de strings, uma por linha:
 *   Linha 1: "Rua das Flores, 123 - Sala 2"
 *   Linha 2: "Centro — São Paulo, SP"
 * Se não houver endereço detalhado, retorna apenas "São Paulo, SP".
 */
function buildLocationLines(company) {
    const lines = [];

    // Linha 1: Rua, número, complemento
    if (company.address) {
        let streetLine = company.address;
        if (company.number) streetLine += `, ${company.number}`;
        if (company.complement) streetLine += ` - ${company.complement}`;
        lines.push(streetLine);
    }

    // Linha 2: Bairro — Cidade, Estado
    const line2Parts = [];
    if (company.district) {
        line2Parts.push(company.district);
    }
    if (company.city && company.state) {
        line2Parts.push(`${company.city}, ${company.state}`);
    } else if (company.city) {
        line2Parts.push(company.city);
    } else if (company.state) {
        line2Parts.push(company.state);
    }
    if (line2Parts.length > 0) {
        lines.push(line2Parts.join(' — '));
    }

    return lines;
}

/**
 * PublicScheduleHeader
 * Topo fixo: logo Gabriela centralizado (linha 1) + separador + identidade da clínica (linha 2).
 * Exibe nome, especialidade, localização e telefone quando disponíveis.
 */
export default function PublicScheduleHeader({ company }) {
    const locationLines = company ? buildLocationLines(company) : [];

    return (
        <header className="ps-header">
            {/* Linha 1: Logo Gabriela centralizado */}
            <div className="ps-header__top">
                <img src={logoFull} alt="Gabriela" className="ps-header__brand-logo-full" />
            </div>

            {/* Separador entre logo Gabriela e identidade da clínica */}
            {company && <div className="ps-header__divider" />}

            {/* Linha 2: Identidade da clínica */}
            {company && (
                <div className="ps-header__clinic-row">
                    {company.logo
                        ? <img src={RemotePath.PATH_USER_UPLOADS_COMPANY_LOGO(company.id) + company.logo} alt={company.name} className="ps-header__clinic-avatar" />
                        : (
                            <div className="ps-header__clinic-avatar ps-header__clinic-avatar--placeholder">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                </svg>
                            </div>
                        )
                    }
                    <div className="ps-header__clinic-info">
                        <span className="ps-header__clinic-name">{company.name}</span>

                        {/* Especialidade / subtítulo */}
                        {company.specialty && (
                            <span className="ps-header__clinic-specialty">{company.specialty}</span>
                        )}

                        {/* Localização: endereço em linhas separadas */}
                        {locationLines.length > 0 && (
                            <span className="ps-header__clinic-meta ps-header__clinic-meta--location">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                <span>
                                    {locationLines.map((line, i) => (
                                        <React.Fragment key={i}>
                                            {i > 0 && <br />}
                                            {line}
                                        </React.Fragment>
                                    ))}
                                </span>
                            </span>
                        )}

                        {/* Telefone */}
                        {company.phone && (
                            <span className="ps-header__clinic-meta">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.13 6.13l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                {company.phone}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
